import { createSessionKeyedReadCache } from '../../runtime/session-read-cache';
import { loadOperationsSummary } from './operations-summary-port';
import { loadSiteInspectionSummary } from './site-inspection-summary-port';
import { loadHeadquartersMeetingSites } from '../communications/meeting-minutes-repository';
import type { BudgetOpexWorkspace } from '@kop/contracts';
import type { MeetingMinutesPage } from '../communications/meeting-minutes-model';

function errorCode(error: unknown) {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : '';
}
function authorityFailure(error: unknown) {
  return [
    'functions/permission-denied',
    'functions/unauthenticated',
    'CALLABLE_READ_ADMISSION_INVALIDATED',
  ].includes(errorCode(error));
}
function temporaryFailure(error: unknown) {
  return [
    'functions/internal',
    'functions/unavailable',
    'functions/resource-exhausted',
    'functions/deadline-exceeded',
    'functions/network-request-failed',
  ].includes(errorCode(error));
}
type SavedSection<T> = {
  state: 'ready' | 'saved' | 'unavailable';
  value: T | null;
  readAt?: number;
};
const siteCache = createSessionKeyedReadCache<{
  value: Awaited<ReturnType<typeof loadHeadquartersMeetingSites>>;
  at: number;
}>(4);
export function peekMonthlyMeetingSites(uid: string) {
  return uid ? (siteCache.peek(uid)?.value ?? null) : null;
}
export async function loadMonthlyMeetingSites(uid: string, force = false) {
  if (!uid) throw Error('IDENTITY_REQUIRED');
  const cached = siteCache.peek(uid);
  try {
    return (
      await siteCache.read(
        uid,
        async () => ({ value: await loadHeadquartersMeetingSites(uid), at: Date.now() }),
        force || !cached || Date.now() - cached.at > 60_000,
      )
    ).value;
  } catch (error) {
    if (authorityFailure(error)) siteCache.invalidate(uid);
    throw error;
  }
}

export function meetingMonthRange(month: string) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/u.test(month)) throw Error('INVALID_MONTH');
  return {
    from: `${month}-01`,
    to: new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)), 0))
      .toISOString()
      .slice(0, 10),
  };
}
export function previousMeetingMonth(month: string) {
  meetingMonthRange(month);
  return new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5)) - 2, 1))
    .toISOString()
    .slice(0, 7);
}
async function settled<T>(
  read: () => Promise<T>,
  previous?: SavedSection<T>,
): Promise<SavedSection<T>> {
  try {
    return { state: 'ready' as const, value: await read(), readAt: Date.now() };
  } catch (error) {
    if (authorityFailure(error)) throw error;
    if (temporaryFailure(error) && previous?.value != null)
      return { state: 'saved', value: previous.value, readAt: previous.readAt };
    return { state: 'unavailable' as const, value: null };
  }
}
async function readMonthlyMeeting(
  uid: string,
  siteId: string | null,
  month: string,
  fresh: boolean,
  previous?: MonthlyMeetingRead | null,
) {
  const period = meetingMonthRange(month);
  const section = <T>(read: () => Promise<T>, saved?: SavedSection<T>) =>
    !fresh && saved?.state === 'ready' && Date.now() - (saved.readAt ?? 0) < 60_000
      ? Promise.resolve(saved)
      : settled(read, saved);
  const [operations, inspection, budget, meetings] = await Promise.all([
    section(() => {
      if (!siteId) throw Error('SITE_OPERATIONS_SUMMARY_UNAVAILABLE');
      return loadOperationsSummary({
        siteId,
        day: new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }),
        month,
        features: ['tbm', 'safety-training', 'evacuation-drill', 'work-permit'],
        fresh,
      });
    }, previous?.operations),
    section(() => {
      if (!siteId) throw Error('SITE_INSPECTION_SUMMARY_UNAVAILABLE');
      return loadSiteInspectionSummary(uid, siteId, period, fresh);
    }, previous?.inspection),
    section(async () => {
      const { createBudgetOpexRepository } =
        await import('../budget-materials/budget-opex-repository');
      const port = createBudgetOpexRepository(null, siteId);
      if (!port) throw Error('UNAVAILABLE');
      const page = await port.load({
        from: month,
        to: month,
        owner: 'principal',
        company: '',
        category: '',
        search: '',
        offset: 0,
      });
      if (!page.complete || page.heldCount !== 0) throw Error('BUDGET_SUMMARY_INCOMPLETE');
      return page;
    }, previous?.budget),
    section(async () => {
      const { createMeetingMinutesRepository } =
        await import('../communications/meeting-minutes-repository');
      return createMeetingMinutesRepository(siteId).list({
        search: '',
        dateFrom: period.from,
        dateTo: period.to,
        kind: 'committee',
        cadence: 'monthly',
        cursor: null,
      });
    }, previous?.meetings),
  ]);
  return { month, operations, inspection, budget, meetings, at: Date.now() };
}
export type MonthlyMeetingRead = {
  month: string;
  operations: SavedSection<Awaited<ReturnType<typeof loadOperationsSummary>>>;
  inspection: SavedSection<Awaited<ReturnType<typeof loadSiteInspectionSummary>>>;
  budget: SavedSection<BudgetOpexWorkspace>;
  meetings: SavedSection<MeetingMinutesPage>;
  at: number;
};
const cache = createSessionKeyedReadCache<MonthlyMeetingRead>(8);
export function peekMonthlyMeeting(
  uid: string,
  siteId: string | null,
  month: string,
): MonthlyMeetingRead | null {
  if (!uid) return null;
  meetingMonthRange(month);
  return cache.peek(`${uid}:${siteId ?? 'no-site'}:${month}`);
}
export async function loadMonthlyMeeting(
  uid: string,
  siteId: string | null,
  month: string,
  force = false,
): Promise<MonthlyMeetingRead> {
  if (!uid) return Promise.reject(Error('IDENTITY_REQUIRED'));
  meetingMonthRange(month);
  const key = `${uid}:${siteId ?? 'no-site'}:${month}`;
  const hit = cache.peek(key);
  const incomplete =
    hit &&
    [hit.operations, hit.inspection, hit.budget, hit.meetings].some(
      (section) => section.state !== 'ready',
    );
  try {
    return await cache.read(
      key,
      () => readMonthlyMeeting(uid, siteId, month, force, hit),
      force || !hit || Date.now() - hit.at > (incomplete ? 5_000 : 60_000),
    );
  } catch (error) {
    if (authorityFailure(error)) cache.invalidate(key);
    throw error;
  }
}
